import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useInView, useReducedMotion } from 'motion/react'
import { cn } from '@/lib/utils'
import { TextReveal, useMediaQuery } from '@/components/motion'
import { HALFTONE_STYLE } from '@/components/landing/about/Highlight'

const EASE = 'ease-[cubic-bezier(0.22,1,0.36,1)]'

export interface TimelineItem {
  title: string
  description: string
  /** Small tag under the copy (the use case's industry). */
  tag: string
  /** Big marker on the tile, e.g. "01". */
  marker: string
  background: string
  /** Product fragment laid over the tile. */
  art: ReactNode
}

/**
 * The about page's journey timeline, adapted for use cases: a sticky heading
 * column whose slot crossfades to the tile in the viewport's centre band,
 * 3:2 tiles with big markers, and the 5px difference-blend centre line with
 * a pink fill whose head sticks to the viewport centre. Below lg (and under
 * reduced motion) everything is static flow with the copy under each tile.
 */
export function UseCaseTimeline({
  heading,
  headingId,
  intro,
  items,
}: {
  heading: ReactNode
  headingId: string
  intro?: ReactNode
  items: TimelineItem[]
}) {
  const desktop = useMediaQuery('(min-width: 1024px)')
  const reduce = useReducedMotion()
  const listRef = useRef<HTMLOListElement>(null)
  const tileRefs = useRef<(HTMLDivElement | null)[]>([])
  const near = useInView(listRef, { margin: '50% 0px 50% 0px' })
  const [active, setActive] = useState(0)
  const [inBand, setInBand] = useState(-1)

  useEffect(() => {
    if (!desktop || !near) return
    let frame = 0
    const measure = () => {
      frame = 0
      const mid = window.innerHeight / 2
      const half = window.innerHeight * 0.05
      let nextActive = 0
      let nextBand = -1
      tileRefs.current.forEach((el, i) => {
        if (!el) return
        const r = el.getBoundingClientRect()
        if (r.top <= mid) nextActive = i
        if (r.top < mid + half && r.bottom > mid - half) nextBand = i
      })
      setActive(nextActive)
      setInBand(nextBand)
    }
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure)
    }
    measure()
    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
    }
  }, [desktop, near])

  const dimmable = desktop && !reduce

  return (
    <section aria-labelledby={headingId} className="landing-section">
      <div className="landing-container grid items-start gap-14 lg:grid-cols-[minmax(0,0.4fr)_minmax(0,1fr)] lg:gap-20">
        <div className="lg:sticky lg:top-28 lg:flex lg:h-[calc(100svh-9rem)] lg:flex-col">
          <TextReveal as="h2" className="landing-text-display-xl text-[var(--color-landing-gray-900)] dark:text-white">
            <span id={headingId}>{heading}</span>
          </TextReveal>
          {intro}

          {/* Crossfading slot (visual; the tiles carry the copy) */}
          <div aria-hidden className="mt-auto hidden pt-10 lg:grid">
            {items.map((item, i) => (
              <div
                key={item.title}
                className={cn('[grid-area:1/1] transition-[opacity,transform] duration-500', EASE)}
                style={{ opacity: i === active ? 1 : 0, transform: i === active ? 'translateY(0)' : 'translateY(10px)' }}
              >
                <p className="font-[family-name:var(--font-landing-display)] text-[1.6rem] uppercase leading-[1.4] tracking-[-0.02em] text-[var(--color-landing-gray-900)] dark:text-white">
                  {item.title}
                </p>
                <span className="mt-4 block h-px w-16 bg-[var(--color-landing-gray-300)] dark:bg-[var(--color-landing-gray-700)]" />
                <p className="landing-text-body mt-4 max-w-[42ch] text-[color-mix(in_srgb,var(--color-landing-gray-900)_66%,transparent)] dark:text-[color-mix(in_srgb,white_62%,transparent)]">
                  {item.tag}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="relative">
          <div aria-hidden className="pointer-events-none absolute inset-y-0 left-1/2 z-10 hidden w-[5px] -translate-x-1/2 bg-white mix-blend-difference lg:block" />
          <div aria-hidden className="pointer-events-none absolute inset-y-0 left-1/2 z-20 hidden w-4 -translate-x-1/2 [overflow:clip] lg:block">
            <div className="sticky top-[50vh] h-0">
              <div className="absolute bottom-0 left-1/2 h-[200vh] w-[5px] -translate-x-1/2 bg-[#ff00f2]" />
              <div className="absolute left-1/2 top-0 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 bg-[#ff00f2]" />
            </div>
          </div>

          <ol ref={listRef} className="grid gap-16 lg:gap-24">
            {items.map((item, index) => (
              <li key={item.title}>
                <div
                  ref={(el) => {
                    tileRefs.current[index] = el
                  }}
                  aria-hidden
                  className={cn(
                    'relative aspect-[3/2] overflow-hidden [container-type:inline-size] transition-opacity duration-700 dark:ring-1 dark:ring-inset dark:ring-white/10',
                    EASE
                  )}
                  style={{ background: item.background, opacity: !dimmable || index === inBand ? 1 : 0.5 }}
                >
                  <div className="absolute inset-0 opacity-50" style={HALFTONE_STYLE} />
                  {item.art}
                  <span className="absolute bottom-[5%] left-[5%] font-[family-name:var(--font-landing-display)] text-[clamp(2.5rem,11cqw,7rem)] uppercase leading-[1.1] tracking-[-0.03em] text-white">
                    {item.marker}
                  </span>
                </div>
                <div className="mt-6">
                  <h3 className="font-[family-name:var(--font-landing-subdisplay)] text-xl font-bold uppercase tracking-[-0.01em] text-[var(--color-landing-gray-900)] dark:text-white md:text-2xl">
                    {item.title}
                  </h3>
                  <p className="landing-text-body mt-3 max-w-[52ch] text-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-300)]">
                    {item.description}
                  </p>
                  <span className="mt-4 inline-block bg-[var(--color-landing-gray-100)] px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-[var(--color-landing-gray-700)] dark:bg-[#1c1c26] dark:text-[var(--color-landing-gray-300)]">
                    {item.tag}
                  </span>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  )
}
